"use client";

import {
  createContext,
  useContext,
  type ReactNode,
} from "react";
import type { BlogPuckData, LandingPuckData } from "@/puck/types";
import {
  DEFAULT_LANGUAGE,
  type SupportedLanguage,
} from "@/utils/library/languages";
import { findPuckBlockProp } from "@/utils/findPuckBlockProp";

type StudioEditingContextValue = {
  editingLang: SupportedLanguage;
  englishDraft: LandingPuckData | BlogPuckData | null;
  getEnglishBlockProp: (blockId: string, propName: string) => string | null;
  /**
   * Write a prop onto the matching block in every locale draft, then remount
   * Puck so the canvas reflects the active locale value without going through
   * English structure-sync (which would overwrite translations).
   */
  applyBlockPropByLocale: (
    blockId: string,
    propName: string,
    valuesByLocale: Partial<Record<SupportedLanguage, string>>,
  ) => void;
};

const StudioEditingContext = createContext<StudioEditingContextValue | null>(
  null,
);

export function useStudioEditing(): StudioEditingContextValue {
  const context = useContext(StudioEditingContext);
  if (!context) {
    throw new Error(
      "useStudioEditing must be used within StudioEditingProvider",
    );
  }
  return context;
}

type StudioEditingProviderProps = {
  editingLang: SupportedLanguage;
  englishDraft: LandingPuckData | BlogPuckData | null | undefined;
  applyBlockPropByLocale: StudioEditingContextValue["applyBlockPropByLocale"];
  children: ReactNode;
};

export function StudioEditingProvider({
  editingLang,
  englishDraft,
  applyBlockPropByLocale,
  children,
}: Readonly<StudioEditingProviderProps>) {
  const draft = englishDraft ?? null;

  const value: StudioEditingContextValue = {
    editingLang,
    englishDraft: draft,
    getEnglishBlockProp: (blockId, propName) => {
      const raw = findPuckBlockProp(draft ?? undefined, blockId, propName);
      return typeof raw === "string" && raw.trim() ? raw : null;
    },
    applyBlockPropByLocale,
  };

  return (
    <StudioEditingContext.Provider value={value}>
      {children}
    </StudioEditingContext.Provider>
  );
}

export function isDefaultStudioEditingLang(lang: SupportedLanguage): boolean {
  return lang === DEFAULT_LANGUAGE;
}
